import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import PrescriptionModal from '@/components/PrescriptionModal';

export default function ExpiringPrescriptions() {
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  useEffect(() => {
    fetchExpiring();
  }, []);

  const fetchExpiring = async () => {
    try {
      const response = await axiosInstance.get('/prescriptions/expiring/soon?days=30');
      setExpiring(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar receitas:', error);
      toast.error('Erro ao carregar receitas');
      setLoading(false);
    }
  };

  const handleRenew = async (item) => {
    try {
      const response = await axiosInstance.post(`/prescription/renew/${item.prescription.id}`);
      toast.success('Receita renovada com sucesso!');
      
      // Abrir modal com a receita renovada para impressão
      setSelectedPatient(item.patient);
      setSelectedPrescription(response.data.prescription);
      setShowPrintModal(true);
      
      fetchExpiring();
    } catch (error) {
      console.error('Erro ao renovar receita:', error);
      toast.error('Erro ao renovar receita');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] flex items-center justify-center">
        <p className="text-lg text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
      <div className="max-w-5xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-2">
            Receitas Próximas do Vencimento
          </h1>
          <p className="text-muted-foreground font-medium">
            {expiring.length} receita(s) vencendo nos próximos 30 dias
          </p>
        </div>

        {expiring.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground py-8">
                Nenhuma receita próxima do vencimento
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {expiring.map((item) => (
              <Card key={item.prescription.id} className="border-l-4 border-l-orange-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{item.patient.nome}</h3>
                        <p className="text-sm text-muted-foreground">
                          {item.days_until_expiry > 0 
                            ? `Vence em ${item.days_until_expiry} dia(s)` 
                            : 'VENCIDA'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Validade: {new Date(item.prescription.data_validade).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge className={item.days_until_expiry <= 0 ? 'bg-red-600' : 'bg-orange-500'}>
                        {item.days_until_expiry <= 0 ? 'Vencida' : 'Vencendo'}
                      </Badge>
                      <Button
                        onClick={() => handleRenew(item)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                        data-testid={`renew-${item.prescription.id}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Renovar
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Medicamentos:</p>
                    <div className="flex flex-wrap gap-2">
                      {item.prescription.medicamentos.slice(0, 3).map((med, idx) => (
                        <Badge key={idx} variant="outline">
                          {med.name}
                        </Badge>
                      ))}
                      {item.prescription.medicamentos.length > 3 && (
                        <Badge variant="outline">
                          +{item.prescription.medicamentos.length - 3} mais
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal para impressão da receita renovada */}
      {showPrintModal && selectedPatient && selectedPrescription && (
        <PrescriptionModal
          open={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          patient={selectedPatient}
          prescription={selectedPrescription}
          onEdit={() => {}}
          onDelete={() => {}}
        />
      )}
    </div>
  );
}